import { DeleteOutlined } from '@ant-design/icons';
import { Button, Flex, Form, Input, Radio, Typography } from 'antd';
import type { RadioChangeEvent } from 'antd';
import React from 'react';

import {
  PRICE_FIELD_CLASS,
  PRICE_VAT_ROW_TESTID,
  PriceVatRow,
} from '@entities/billing/modals/styled';
import { grossPrice, VAT_RATE, VAT_RATE_CHOICES } from '@entities/billing/vat';

type Row = { unitPrice?: number; totalPrice?: number; taxRate?: number };

type InvoiceItemsFormListProps = {
  /** Form.List field name. */
  name?: string;
  /** Singular noun for the row heading and add button ("Item", "Line"). */
  itemNoun?: string;
};

/**
 * Editable list of invoice line items (name / description / price / VAT rate)
 * with the VAT-inclusive total auto-filled from the pre-VAT price and the
 * selected rate. Shared by the one-time invoice and credit note modals; must be
 * rendered inside a Form.
 */
export const InvoiceItemsFormList = ({
  name = 'items',
  itemNoun = 'Item',
}: InvoiceItemsFormListProps) => {
  const form = Form.useFormInstance();

  // Recompute the row total from its current price and rate whenever either of
  // them changes, so the stored total always matches the rate we later submit.
  const recalcRow = (fieldName: number, patch: Partial<Row>) => {
    const items = (form.getFieldValue(name) as Row[]) || [];
    const row = { ...items[fieldName], ...patch };
    const unitPrice = Number(row.unitPrice);
    const taxRate = row.taxRate ?? VAT_RATE;
    items[fieldName] = {
      ...row,
      totalPrice: Number.isFinite(unitPrice)
        ? grossPrice(unitPrice, taxRate)
        : undefined,
    };
    form.setFieldsValue({ [name]: items });
  };

  const updateUnitPrice =
    (fieldName: number) => (e: React.ChangeEvent<HTMLInputElement>) =>
      recalcRow(fieldName, { unitPrice: +e.target.value });

  const updateTaxRate = (fieldName: number) => (e: RadioChangeEvent) =>
    recalcRow(fieldName, { taxRate: e.target.value as number });

  return (
    <Form.List name={name}>
      {(fields, { add, remove }) => (
        <Flex vertical gap={8}>
          {fields.map((field, index) => (
            <Flex key={field.key} vertical>
              <Flex justify="space-between">
                <Typography.Title level={5}>
                  {itemNoun} {index + 1}
                </Typography.Title>
                {fields.length > 1 && (
                  <Button
                    type="text"
                    icon={<DeleteOutlined />}
                    onClick={() => remove(index)}
                    danger
                  />
                )}
              </Flex>
              <Form.Item
                name={[field.name, 'name']}
                label="Name"
                rules={[{ required: true }]}
              >
                <Input size="large" />
              </Form.Item>
              <Form.Item
                name={[field.name, 'description']}
                label="Description (optional)"
              >
                <Input.TextArea />
              </Form.Item>
              <PriceVatRow gap={16} wrap data-testid={PRICE_VAT_ROW_TESTID}>
                <Form.Item
                  className={PRICE_FIELD_CLASS}
                  name={[field.name, 'unitPrice']}
                  label="Price before VAT"
                  rules={[{ required: true }]}
                >
                  <Input
                    type="number"
                    step="0.01"
                    size="large"
                    onChange={updateUnitPrice(field.name)}
                  />
                </Form.Item>
                <Form.Item
                  name={[field.name, 'taxRate']}
                  label="VAT rate"
                  initialValue={VAT_RATE}
                  rules={[{ required: true }]}
                >
                  <Radio.Group
                    options={VAT_RATE_CHOICES}
                    optionType="button"
                    onChange={updateTaxRate(field.name)}
                  />
                </Form.Item>
              </PriceVatRow>
              <Form.Item name={[field.name, 'totalPrice']} label="Price">
                <Input type="number" size="large" disabled />
              </Form.Item>
            </Flex>
          ))}
          <Button type="link" onClick={() => add({ taxRate: VAT_RATE })}>
            Add {itemNoun.toLowerCase()}
          </Button>
        </Flex>
      )}
    </Form.List>
  );
};
